/**
 * Contact Support Modal Component
 *
 * Displayed when user clicks "Not Me" on the acknowledgment screen.
 * Provides contact information and next steps.
 */
import { motion } from 'framer-motion';
import { AlertTriangle, Mail, Phone, X } from 'lucide-react';
import { Button, Dialog, DialogContent, DialogFooter } from '@respectly/ui';

interface ContactSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  supportEmail?: string;
  supportPhone?: string;
}

export function ContactSupportModal({
  isOpen,
  onClose,
  supportEmail = 'support@example.com',
  supportPhone,
}: ContactSupportModalProps) {
  const handleClose = () => {
    onClose();
  };

  const handleCloseTab = () => {
    // Try to close the tab, fallback to showing a message
    window.close();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent showCloseButton={false} className="max-w-md">
        <div className="space-y-6">
          {/* Warning Icon */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex justify-center"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
              <AlertTriangle className="h-8 w-8 text-amber-600" />
            </div>
          </motion.div>

          {/* Title */}
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900">
              Wrong Person?
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              If this signing request was not intended for you, please contact
              the sender or our support team to resolve this issue.
            </p>
          </div>

          {/* Contact Info */}
          <div className="rounded-lg bg-gray-50 p-4 space-y-3">
            <h3 className="text-sm font-medium text-gray-700">
              Contact Support
            </h3>
            <div className="space-y-2">
              <a
                href={`mailto:${supportEmail}`}
                className="flex items-center gap-3 text-sm text-gray-600 hover:text-blue-600 transition-colors"
              >
                <Mail className="h-4 w-4 text-gray-400" />
                <span>{supportEmail}</span>
              </a>
              {supportPhone && (
                <a
                  href={`tel:${supportPhone}`}
                  className="flex items-center gap-3 text-sm text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span>{supportPhone}</span>
                </a>
              )}
            </div>
          </div>

          {/* Info Note */}
          <p className="text-xs text-center text-gray-500">
            Do not sign documents that are not intended for you. Contact the
            sender if you received this link in error.
          </p>

          {/* Action Buttons */}
          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 sm:flex-none"
            >
              Go Back
            </Button>
            <Button
              variant="default"
              onClick={handleCloseTab}
              className="flex-1 sm:flex-none"
            >
              <X className="mr-2 h-4 w-4" />
              Close Page
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
